import React, { useEffect, useRef, useCallback } from 'react';
import { Stage, Layer } from 'react-konva';
import Konva from 'konva';
import { useShallow } from 'zustand/react/shallow';
import { useEditorStore, CELL, PPM, RackNode } from '../../store/editorStore';
import { createFloorEntityDefaults } from '../../utils/floorEntities.logic';
import { snapToGrid, TILE_PX } from '../../utils/cadSnapping.logic';
import { findNearestFreePosition, Box2D } from '../../utils/collisionGuard.logic';
import { getTrayMediaConfig, calculateTrayMaxCables } from '../../utils/traySegregation.logic';
import { GridLayer } from './GridLayer';
import { RacksLayer } from './RacksLayer';
import { TraysLayer } from './TraysLayer';
import { LensLayer } from './LensLayer';
import { OverlayLayer } from './OverlayLayer';
import { TooltipOverlay } from './TooltipOverlay';
import { LensLegend } from './LensLegend';
import { ElevationPanel } from './ElevationPanel';
import { useCanvasControls } from '../../hooks/useCanvasControls';
import { AislesLayer } from './AislesLayer';
import type { AisleType } from '../../store/editorStore';
import { useTelemetryStream } from '../../hooks/useTelemetryStream';

interface DataCenterCanvasProps {
  dcCode?: string;
  stageRef?: React.RefObject<any>;
}

function genId() {
  return Math.random().toString(36).slice(2, 10);
}

export const DataCenterCanvas: React.FC<DataCenterCanvasProps> = ({
  dcCode,
  stageRef: externalStageRef,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const internalStageRef = useRef<any>(null);
  const stageRef = externalStageRef || internalStageRef;

  const {
    stageX, stageY, stageScale,
    stageW, stageH,
    clearSelection,
    tool,
    selectedIds, setSelectedIds,
    racks,
    setMarquee,
    addTrayNode, addTrayEdge,
    drawingTrayFromId, setDrawingTrayFromId,
    addAisleZone,
    activeLens,
    setTelemetryData,
    setCollidingIds,
  } = useEditorStore(useShallow(state => ({
    stageX: state.stageX, stageY: state.stageY, stageScale: state.stageScale,
    stageW: state.stageW, stageH: state.stageH,
    clearSelection: state.clearSelection,
    tool: state.tool,
    selectedIds: state.selectedIds, setSelectedIds: state.setSelectedIds,
    racks: state.racks,
    setMarquee: state.setMarquee,
    addTrayNode: state.addTrayNode, addTrayEdge: state.addTrayEdge,
    drawingTrayFromId: state.drawingTrayFromId, setDrawingTrayFromId: state.setDrawingTrayFromId,
    addAisleZone: state.addAisleZone,
    activeLens: state.activeLens,
    setTelemetryData: state.setTelemetryData,
    setCollidingIds: state.setCollidingIds,
  })));

  const workerRef = useRef<Worker | null>(null);

  // WebWorker for collision detection: instantiate once
  useEffect(() => {
    workerRef.current = new Worker(
      new URL('../../workers/collisionWorker.ts', import.meta.url),
      { type: 'module' }
    );
    
    workerRef.current.onmessage = (e) => {
      setCollidingIds(e.data.collidingIds || []);
    };

    return () => {
      workerRef.current?.terminate();
    };
  }, [setCollidingIds]);

  // Post updates to worker when racks configuration changes
  useEffect(() => {
    if (!workerRef.current) return;
    const rackList = Object.values(racks).map(r => ({
      id: r.id,
      x: r.x,
      y: r.y,
      w: (r.widthM || 0.6) * 100,
      h: (r.depthM || 1.0) * 100,
      rotation: r.rotation || 0
    }));
    workerRef.current.postMessage({ racks: rackList });
  }, [racks]);

  // Telemetry real-time sync when a lens is active
  const isLensActive = activeLens !== 'none';
  const liveMetrics = useTelemetryStream(isLensActive ? dcCode : undefined, undefined);

  useEffect(() => {
    if (isLensActive && liveMetrics && Object.keys(liveMetrics).length > 0) {
      setTelemetryData(liveMetrics);
    }
  }, [liveMetrics, isLensActive, setTelemetryData]);

  // Zone drawing state (ephemeral, not in store)
  const zoneDrawStart = useRef<{ x: number; y: number } | null>(null);
  const [zoneDraft, setZoneDraft] = React.useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const pendingZoneType = useRef<AisleType>('cold');
  const {
    handleWheel,
    handleStageDragEnd,
    screenToWorld,
    ghostPos,
    setGhostPos,
    marqueeStart,
    isDraggingMarquee,
  } = useCanvasControls(containerRef);


  // ── Mouse down on stage (marquee / tray / erase) ─────────
  const handleStageMouseDown = useCallback((e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
    if (e.target !== e.target.getStage()) return; // hit a shape, not empty stage

    if (tool === 'select') {
      const ptr = e.target.getStage()?.getPointerPosition();
      if (!ptr) return;
      const w = screenToWorld(ptr.x, ptr.y);
      marqueeStart.current = w;
      isDraggingMarquee.current = false;
    }

    if (tool === 'draw_zone') {
      const ptr = e.target.getStage()?.getPointerPosition();
      if (!ptr) return;
      const w = screenToWorld(ptr.x, ptr.y);
      zoneDrawStart.current = { x: w.x, y: w.y };
      setZoneDraft({ x: w.x, y: w.y, w: 0, h: 0 });
    }

    if (tool === 'draw_tray') {
      // Place or connect tray node on empty stage click
      const stage = e.target.getStage();
      const ptr = stage?.getPointerPosition();
      if (!ptr) return;
      const w = screenToWorld(ptr.x, ptr.y);
      let snappedX = Math.round(w.x / 20) * 20;
      let snappedY = Math.round(w.y / 20) * 20;
      
      const state = useEditorStore.getState();
      const fromNode = drawingTrayFromId ? state.trayNodes[drawingTrayFromId] : null;
      const SNAP_DIST = 15;

      // 1. Magnetic Snapping to existing Tray Nodes (prioritize reusing nodes)
      let targetNodeId: string | null = null;
      for (const n of Object.values(state.trayNodes)) {
        if (Math.hypot(w.x - n.x, w.y - n.y) < SNAP_DIST) {
          snappedX = n.x;
          snappedY = n.y;
          targetNodeId = n.id;
          break;
        }
      }

      // 2. Magnetic Snapping to Rack Centers if no existing tray node
      if (!targetNodeId) {
        for (const r of Object.values(state.racks)) {
          const rw = (r.widthM || 0.6) * 100;
          const rh = (r.depthM || 1.0) * 100;
          const cx = r.x + rw / 2;
          const cy = r.y + rh / 2;
          if (Math.abs(w.x - cx) < SNAP_DIST) snappedX = cx;
          if (Math.abs(w.y - cy) < SNAP_DIST) snappedY = cy;
        }
      }

      // 3. Ortho Mode (Shift key)
      if (e.evt.shiftKey && fromNode) {
        if (Math.abs(w.x - fromNode.x) > Math.abs(w.y - fromNode.y)) {
          snappedY = fromNode.y;
        } else {
          snappedX = fromNode.x;
        }
      }
      
      const fromId = drawingTrayFromId;
      const effectiveNodeId = targetNodeId || genId();
      if (!targetNodeId) {
        addTrayNode({ id: effectiveNodeId, x: snappedX, y: snappedY, type: 'terminal' });
      }

      if (fromId && fromId !== effectiveNodeId) {
        const media = useEditorStore.getState().trayDrawType;
        const config = getTrayMediaConfig(media);
        addTrayEdge({
          id: genId(),
          source: fromId,
          target: effectiveNodeId,
          color: config.color,
          type: media,
          mediaType: media,
          trayType: config.defaultType,
          widthM: 0.3,
          maxCables: calculateTrayMaxCables(0.3, media),
        });
      }
      setDrawingTrayFromId(effectiveNodeId);
      setGhostPos({ x: snappedX, y: snappedY });
    }
  }, [tool, screenToWorld, drawingTrayFromId, addTrayNode, addTrayEdge, setDrawingTrayFromId, setGhostPos]);

  // ── Mouse move (ghost line + marquee rect) ───────────────
  const handleStageMouseMove = useCallback((e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
    const ptr = e.target.getStage()?.getPointerPosition();
    if (!ptr) return;
    const w = screenToWorld(ptr.x, ptr.y);

    if (tool === 'draw_tray' && drawingTrayFromId) {
      let snapX = Math.round(w.x / 20) * 20;
      let snapY = Math.round(w.y / 20) * 20;
      
      const state = useEditorStore.getState();
      const fromNode = state.trayNodes[drawingTrayFromId];
      const SNAP_DIST = 15;
      
      // 1. Magnetic Snapping to existing Tray Nodes
      let snappedToNode = false;
      for (const n of Object.values(state.trayNodes)) {
        if (Math.hypot(w.x - n.x, w.y - n.y) < SNAP_DIST) {
          snapX = n.x;
          snapY = n.y;
          snappedToNode = true;
          break;
        }
      }

      // 2. Magnetic Snapping to Rack Centers (if not snapped to a node)
      if (!snappedToNode) {
        for (const r of Object.values(state.racks)) {
          const rw = (r.widthM || 0.6) * 100;
          const rh = (r.depthM || 1.0) * 100;
          const cx = r.x + rw / 2;
          const cy = r.y + rh / 2;
          if (Math.abs(w.x - cx) < SNAP_DIST) snapX = cx;
          if (Math.abs(w.y - cy) < SNAP_DIST) snapY = cy;
        }
      }
      
      // 3. Ortho Mode (Shift key)
      if (e.evt.shiftKey && fromNode) {
        if (Math.abs(w.x - fromNode.x) > Math.abs(w.y - fromNode.y)) {
          snapY = fromNode.y;
        } else {
          snapX = fromNode.x;
        }
      }
      
      setGhostPos({ x: snapX, y: snapY });
    }

    // Zone drawing preview
    if (tool === 'draw_zone' && zoneDrawStart.current) {
      const sx = zoneDrawStart.current.x;
      const sy = zoneDrawStart.current.y;
      setZoneDraft({
        x: Math.min(sx, w.x),
        y: Math.min(sy, w.y),
        w: Math.abs(w.x - sx),
        h: Math.abs(w.y - sy),
      });
    }

    if (tool === 'select' && marqueeStart.current) {
      isDraggingMarquee.current = true;
      const mx = Math.min(marqueeStart.current.x, w.x);
      const my = Math.min(marqueeStart.current.y, w.y);
      const mw = Math.abs(w.x - marqueeStart.current.x);
      const mh = Math.abs(w.y - marqueeStart.current.y);
      if (mw > 4 || mh > 4) {
        setMarquee({ x: mx, y: my, w: mw, h: mh });
      }
    }
  }, [tool, drawingTrayFromId, screenToWorld, setGhostPos, setMarquee]);

  // ── Mouse up (finish marquee select) ────────────────────
  const handleStageMouseUp = useCallback((e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
    if (tool === 'select' && isDraggingMarquee.current && marqueeStart.current) {
      const stage = e.target.getStage();
      const ptr = stage?.getPointerPosition();
      if (!ptr) return;
      const w = screenToWorld(ptr.x, ptr.y);
      const rx = Math.min(marqueeStart.current.x, w.x);
      const ry = Math.min(marqueeStart.current.y, w.y);
      const rw = Math.abs(w.x - marqueeStart.current.x);
      const rh = Math.abs(w.y - marqueeStart.current.y);

      const RACK_W = (rack: RackNode) => (rack.widthM || 0.6) * 100;
      const RACK_H = (rack: RackNode) => (rack.depthM || 1.0) * 100;
      const hits = Object.values(racks).filter(rack =>
        rack.x < rx + rw &&
        rack.x + RACK_W(rack) > rx &&
        rack.y < ry + rh &&
        rack.y + RACK_H(rack) > ry
      );
      setSelectedIds(hits.map(r => r.id));
      setMarquee(null);
    }

    // Finish zone draw
    if (tool === 'draw_zone' && zoneDraft && zoneDraft.w > 20 && zoneDraft.h > 20) {
      const id = genId();
      addAisleZone({
        id,
        x: zoneDraft.x,
        y: zoneDraft.y,
        w: zoneDraft.w,
        h: zoneDraft.h,
        aisleType: pendingZoneType.current,
        isDirty: true,
      });
    }
    zoneDrawStart.current = null;
    setZoneDraft(null);

    marqueeStart.current = null;
    isDraggingMarquee.current = false;
  }, [tool, screenToWorld, racks, setSelectedIds, setMarquee, zoneDraft, addAisleZone]);

  // ── Click on empty stage (deselect) ─────────────────────
  const handleStageClick = useCallback((e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
    if (e.target !== e.target.getStage()) return;
    if (!isDraggingMarquee.current) {
      clearSelection();
    }
  }, [clearSelection]);

  // ── Drop from ObjectPalette ──────────────────────────────
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!stageRef.current) return;

    const rect = (containerRef.current as HTMLElement).getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    const w = screenToWorld(sx, sy);
    const snapX = snapToGrid(w.x, TILE_PX);
    const snapY = snapToGrid(w.y, TILE_PX);

    // Zone drop
    const zoneType = e.dataTransfer.getData('zoneType') as AisleType | '';
    if (zoneType) {
      const ZONE_W = CELL * 3;
      const ZONE_H = CELL * 2;
      addAisleZone({
        id: genId(),
        x: snapX - ZONE_W / 2,
        y: snapY - ZONE_H / 2,
        w: ZONE_W,
        h: ZONE_H,
        aisleType: zoneType,
        isDirty: true,
      });
      return;
    }

    // Floor Entity drop (IT Rack, Facility Unit, Pillar)
    const rackType = e.dataTransfer.getData('rackType');
    if (!rackType) return;

    const defaults = createFloorEntityDefaults(rackType);
    const seq = useEditorStore.getState()._paletteSeq;
    const id = genId();
    const label = `${defaults.label} ${seq - 10000 + 1}`;
    const candW = Math.round(defaults.width_m * PPM);
    const candH = Math.round(defaults.depth_m * PPM);

    const existingBoxes: Box2D[] = Object.values(racks).map(r => ({
      id: r.id,
      x: r.x,
      y: r.y,
      w: (r.widthM || 0.6) * PPM,
      h: (r.depthM || 1.0) * PPM,
      rotation: r.rotation || 0,
    }));

    const candidateBox: Box2D = {
      id,
      x: snapX,
      y: snapY,
      w: candW,
      h: candH,
      rotation: 0,
    };

    // Collision Guard: if dropped onto an occupied tile, automatically find the nearest free tile
    const placedPos = findNearestFreePosition(candidateBox, existingBoxes, TILE_PX, 6);

    useEditorStore.getState().addRack({
      id,
      x: placedPos.x,
      y: placedPos.y,
      width: candW,
      height: candH,
      rotation: 0,
      orientation: 0,
      label,
      rackType: rackType as any,
      uCount: defaults.u_slots,
      isFacility: defaults.is_facility,
      isBlocked: defaults.is_blocked,
      widthM: defaults.width_m,
      depthM: defaults.depth_m,
      heightM: defaults.height_m,
      tenant: 'mine',
      isDirty: true,
      rowIndex: 10000,
      rackIndex: seq,
    });
    setSelectedIds([id]);
  }, [screenToWorld, setSelectedIds, addAisleZone, racks]);


  const isDraggingStage = tool === 'select' && selectedIds.length === 0;

  const showElevation = useEditorStore(useShallow(state => state.showElevation));

  return (
    <div
      style={{
        display: 'flex', flexDirection: 'column',
        width: '100%', height: '100%',
        background: '#0d1117',
        overflow: 'hidden',
      }}
    >
      {/* Main canvas area */}
      <div
        ref={containerRef}
        style={{
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
          cursor: tool === 'draw_tray' ? 'crosshair' : tool === 'erase' ? 'not-allowed' : 'default',
        }}
        onDrop={handleDrop}
        onDragOver={e => e.preventDefault()}
      >
        {stageW > 0 && stageH > 0 && (
          <Stage
            ref={stageRef}
            width={stageW}
            height={stageH}
            x={stageX}
            y={stageY}
            scaleX={stageScale}
            scaleY={stageScale}
            draggable={isDraggingStage}
            onWheel={handleWheel}
            onDragEnd={handleStageDragEnd}
            onClick={handleStageClick}
            onMouseDown={handleStageMouseDown}
            onMouseMove={handleStageMouseMove}
            onMouseUp={handleStageMouseUp}
            onContextMenu={e => {
              e.evt.preventDefault();
              if (drawingTrayFromId) {
                setDrawingTrayFromId(null);
                setGhostPos(null);
              }
            }}
          >
            <Layer>
              <AislesLayer />
              <GridLayer />
              <TraysLayer ghostPos={ghostPos} dcCode={dcCode || ''} />
              <RacksLayer />
              <LensLayer />
              <OverlayLayer />
            </Layer>
          </Stage>
        )}

        <TooltipOverlay stageRef={stageRef} />
        <LensLegend />
      </div>

      {/* Z-view elevation panel */}
      {showElevation && <ElevationPanel height={160} />}
    </div>
  );
};
